import React, { Component } from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import cn from 'classnames';
import ProgressBar from 'components/ProgressBar';
import scss from 'components/shared.module.scss';
import { Dispatch } from 'redux';
import { UPDATE_ENROLLMENTS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';

import AcadlyLogo from 'assets/acadly-icon.png';
import ErrorIcon from 'assets/error.png';

import UserAvatar from '../UserAvatar';
import { Button } from '../UtilElements';

interface GetEnrolledStudentsResponse extends MessageResponse, CourseEnrollments {}

interface CourseImportProps {
  course: CourseDetails | null;
  onNext(): void;
  updateEnrollments(data: CourseEnrollments): Promise<void>;
}

interface CourseImportState {
  isInProgres: boolean;
}

class CourseImport extends Component<CourseImportProps, CourseImportState> {
  state: CourseImportState = {
    isInProgres: false,
  };

  fetchEnrollments = (courseId: string) => {
    return request<GetEnrolledStudentsResponse>({
      url: API.COURSE_CREATE_IMPORT_STUDENTS_STATUS(courseId),
      method: 'GET',
    });
  };

  componentDidMount() {
    const course = this.props.course;
    if (!course) return;

    if (course.enrolments.importStatus === 'inprogress') {
      // check status every 5 seconds, if done, then stop
      const timer = setInterval(async () => {
        try {
          this.setState({ isInProgres: true });
          const { message: _message, ...enrollments } = await this.fetchEnrollments(course.courseId);
          this.props.updateEnrollments(enrollments);
          if (enrollments.importStatus === 'done') {
            clearInterval(timer);
            this.setState({ isInProgres: false });
          }
        } catch (err) {
          console.error(err);
          clearInterval(timer);
          this.setState({ isInProgres: false });
        }
      }, 5000);
    }
  }

  getStudentsList = () => {
    if (!this.props.course) return null;

    switch (this.props.course.enrolments.importStatus) {
      case 'inprogress':
        return this.progressBox();
      case 'done':
        return this.getEnrolledStudents();
      default:
        return null;
    }
  };

  progressBox = () => {
    const enrolments = this.props.course?.enrolments;
    if (!enrolments) return null;

    return (
      <>
        <label className='import-course__label'>Importing enrolled students</label>
        <ProgressBar
          className='import-course__gutters'
          progress={{ current: enrolments.studentsImported, total: enrolments.totalStudents }}
          progressText={`${enrolments.studentsImported}/${enrolments.totalStudents} imported`}
          message='Please wait while we are importing enrolment data'
        />
      </>
    );
  };

  getEnrolledStudents = () => {
    const enrolments = this.props.course?.enrolments;
    if (!enrolments) return null;

    return (
      <>
        <label className='import-course__label'>{enrolments.totalStudents} students enrolled</label>
        {enrolments.totalStudents ? (
          <TransitionGroup>
            {enrolments.students.map((s) => (
              <CSSTransition key={s.userId} classNames='fade' timeout={300}>
                <>
                  <UserAvatar className='import-course__gutters' {...s} hasActions>
                    {s.hasAcadlyAccount === 1 ? (
                      <div className='has-tooltip'>
                        <div className='tooltip-trigger left' data-tooltip='Has Acadly account'>
                          <img
                            src={AcadlyLogo}
                            className={scss['acadly-inline-icon']}
                            alt='Has Acadly account'
                          />
                        </div>
                      </div>
                    ) : null}
                  </UserAvatar>
                  {!s.valid && (
                    <div key={s.userId + 'error'} className='import-course__error'>
                      <img src={ErrorIcon} alt='' />
                      <span>{s.invalidReason}</span>
                    </div>
                  )}
                </>
              </CSSTransition>
            ))}
          </TransitionGroup>
        ) : (
          <p className='import-course__error'>
            The course doesn’t have any enrollments yet. You can continue importing the course into Acadly and
            sync enrollments later.
          </p>
        )}
      </>
    );
  };

  render() {
    const enrolments = this.props.course?.enrolments;
    if (!enrolments) return null;

    return (
      <>
        <div className='import-course__form'>{this.getStudentsList()}</div>
        <section className='import-course__info'>
          <h2 className='import-course__title'>Step 4 - Import Student Data</h2>
          <p className='import-course__description'>
            Here, Acadly imports the data of the students enrolled in the course. We use this information to
            automatically create accounts for the students (if the accounts don't already exist). Once the
            import process is complete, you can log in to Acadly and 'Go Live' with the course to inform the
            students to sign up.
          </p>
          <div className='import-course__actions'>
            <Button
              type='colored'
              label='Save and Continue'
              selector={cn('btn-primary', 'btn-large', 'btn-save-continue', {
                'btn-disabled': enrolments.importStatus !== 'done',
              })}
              onRipple={this.props.onNext}
            />
          </div>
        </section>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  course: state.courseInCreation,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateEnrollments: async (data: CourseEnrollments) => {
    dispatch({
      type: UPDATE_ENROLLMENTS,
      payload: data,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseImport);
